import { lazy, useEffect, useState } from 'react';
import { Nami, NamiLanguageCodes, NamiLogLevel } from '@namiml/web-sdk';
import './App.css';
import Header from './components/Header';
import CampaignsPage from './pages/Campaigns/CampaignsPage';
import EntitlementsPage from './pages/EntitlementsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import ApiTrafficPage from './pages/ApiTrafficPage';
import PaywallPage from './pages/PaywallPage';
import { NavigationProvider, useNavigation } from './navigation/NavigationProvider';
import { loadSDKConfig, resolveImplies, getHostURL } from './config/SDKConfigReader';
import { loadInitialConfigFromFile, loadInitialConfigFromNetwork } from './config/getInitialConfig';
import { dispatchSdkStateChange } from './hooks/useSdkInitialized';
import { NAMI_SDK_STATE_KEY } from './utils/const';
import { apiTrafficStore, API_TRAFFIC_COMMAND } from './api-traffic';
const EmbedPaywallPage = lazy(() => import('./pages/EmbedPaywallPage'));

const ScreenRouter = () => {
  const { path, navigate } = useNavigation();
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'GoBack' || e.code === 'Escape') {
        e.stopPropagation();
        const route = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
        if (route !== '' && route !== '/') {
          window.history.back();
        }
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, path]);

  if (path.startsWith('/embed')) {
    return <EmbedPaywallPage />;
  }

  if (path.startsWith('/paywall')) {
    return <PaywallPage />;
  }

  switch (path) {
    case '/':
      return <CampaignsPage />;
    case '/profile':
      return <ProfilePage />;
    case '/entitlements':
      return <EntitlementsPage />;
    case '/settings':
      return <SettingsPage />;
    case '/api-traffic':
      return <ApiTrafficPage />;
    case '/embed':
      return <EmbedPaywallPage />;
    default:
      return <CampaignsPage />;
  }
};

function App() {
  const [ready, setReady] = useState(false);

  // Before anything configures the SDK, so the startup fan-out is captured. Registration
  // is idempotent; `start()` returns immediately on a second call.
  useEffect(() => {
    apiTrafficStore.start();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function tryAutoConfigure() {
      try {
        const config = await loadSDKConfig();

        if (config.auto_configure) {
          // Build namiCommands from schema
          const commands: string[] = [];

          const impliedCommands = await resolveImplies(config.connection.nami_host);
          commands.push(...impliedCommands);

          // The SDK gates traffic capture on this command. Registering the observer is not
          // enough on its own (NAM-4447).
          commands.push(API_TRAFFIC_COMMAND);

          // For non-implied hosts, set the API host URL
          if (!impliedCommands.length) {
            const url = await getHostURL(config.connection.nami_host);
            if (url) {
              commands.push(`namiAPIHost=${url}`);
            }
          }

          if (config.debug.validate_product_groups) {
            commands.push('validateProductGroups');
          }
          if (config.debug.disable_campaign_updates) {
            commands.push('disableCampaignUpdates');
          }
          if (config.debug.disable_journey_delivery) {
            commands.push('disableJourneyDelivery');
          }
          if (config.testing.split_position_override !== null) {
            commands.push(`splitPosition=${config.testing.split_position_override}`);
          }

          const appPlatformID = config.connection.custom_app_platform_id || config.connection.app_platform_id;
          const formFactor = config.sdk_settings.form_factor || 'television';

          // b64_api mode fetches the initial config over the network (60s
          // timeout + 1 retry on timeout). On failure we configure without it,
          // preserving the existing fallback behavior of this path.
          let initialConfig: string | undefined;
          if (config.sdk_settings.initial_config_mode === 'b64_api') {
            initialConfig =
              (await loadInitialConfigFromNetwork(
                config.connection.nami_host,
                appPlatformID,
                formFactor,
              )) ?? undefined;
          } else if (config.sdk_settings.initial_config_mode === 'b64_file') {
            // Frozen fixture bundled in public/ (injected by the E2E harness) —
            // keeps suite content deterministic and offline (NAM-2912).
            initialConfig = (await loadInitialConfigFromFile()) ?? undefined;
          }

          const state = await Nami.configure({
            appPlatformID,
            namiLanguageCode: config.sdk_settings.language_code as NamiLanguageCodes,
            logLevel: config.sdk_settings.log_level as NamiLogLevel,
            maxLogging: config.debug.max_logging ?? false,
            ...(initialConfig && { initialConfig }),
            namiCommands: commands,
            formFactor,
          });

          // Annotates the transcript with what configure() returned, which is what the e2e
          // `configure_state` predicate reads (NAM-4606).
          apiTrafficStore.markConfigureResult(state.configureState);

          localStorage.setItem(NAMI_SDK_STATE_KEY, JSON.stringify(state));
          dispatchSdkStateChange();

          if (state.sdkInitialized) {
            console.log('[App] Auto-configured SDK successfully');
          } else {
            console.warn('[App] Auto-configure completed but SDK not initialized');
          }
        }
      } catch (err) {
        console.warn('[App] Auto-configure failed, falling back to manual config', err);
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    }

    tryAutoConfigure();
    return () => { cancelled = true; };
  }, []);

  if (!ready) {
    return null;
  }

  return (
    <NavigationProvider>
      <div className="app-shell">
        <Header />
        <main>
          <ScreenRouter />
        </main>
      </div>
    </NavigationProvider>
  );
}

export default App;
